import React, { useState } from "react";
import QuestionDisplay from "./QuestionDisplay";
import MatchingQuestionsGroup from "./MatchingQuestionsGroup";
import { FaSearchPlus, FaSearchMinus, FaUndo } from "react-icons/fa";

// ===== TYPE DEFINITIONS =====

type QuestionType =
  | "MULTIPLE_CHOICE"
  | "MULTIPLE_CHOICE_4"
  | "TRUE_FALSE"
  | "TEXT_INPUT"
  | "ESSAY"
  | "EMAIL"
  | "LETTER"
  | "AUDIO_MULTIPLE_CHOICE"
  | "AUDIO_TRUE_FALSE"
  | "MATCHING"
  | "DRAG_DROP"
  | "FILL_BLANKS"
  | "SENTENCE_ORDER";

type ExamPart = {
  id: number;
  examSetId: number;
  module: string;
  partNumber: number;
  displayOrder: number;
  instructions: string;
  partTitle?: string;

  // Shared context (applies to ALL questions in this Teil)
  contextText?: string;
  contextImageUrl?: string;
  contextImageDownloadUrl?: string;
  contextDocumentUrl?: string;
  contextDocumentDownloadUrl?: string;
  contextAudioUrl?: string;
  contextAudioDownloadUrl?: string;

  // Metadata
  recommendedTimeMinutes?: number;
  totalPoints: number;
  questionCount: number;

  // Display names
  displayName: string;
  shortDisplayName: string;

  // Helper flags
  hasContextText: boolean;
  hasContextImage: boolean;
  hasContextDocument: boolean;
  hasContextAudio: boolean;
  hasSharedContext: boolean;
};

type Question = {
  id: number;
  examSetId: number;
  examPartId: number;
  module: string;
  partNumber: number;
  questionNumber: number;
  questionType: QuestionType;
  questionText: string;

  contextText?: string;
  audioUrl?: string;
  audioDownloadUrl?: string;
  imageUrl?: string;
  imageDownloadUrl?: string;

  effectiveContextText?: string;
  effectiveAudioUrl?: string;
  effectiveAudioDownloadUrl?: string;

  options: string[] | string;
  points: number;

  usesSharedContext: boolean;
  hasQuestionAudio: boolean;
  hasQuestionImage: boolean;

  displayLabel: string;
  fullDisplayLabel: string;
  isAutoGradable: boolean;

  matchingGroupId?: string;
};

interface TeilDisplayProps {
  part: ExamPart;
  questions: Question[];
  answers: Map<number, string>;
  onAnswerChange: (questionId: number, answer: string) => void;
  disabled?: boolean;
}

/**
 * TeilDisplay Component
 * ✅ UPDATED:
 * - Full-width images on mobile
 * - Inline PDF preview with zoom controls
 */
const TeilDisplay: React.FC<TeilDisplayProps> = ({
  part,
  questions,
  answers,
  onAnswerChange,
  disabled = false,
}) => {
  const answeredCount = questions.filter((q) => answers.has(q.id)).length;
  const allAnswered = answeredCount === questions.length;

  // PDF Zoom state
  const [pdfZoom, setPdfZoom] = useState(100); // Default 100%

  // Image Zoom state
  const [imageZoom, setImageZoom] = useState(100); // Default 100%

  const handlePdfZoomIn = () => {
    setPdfZoom((prev) => Math.min(prev + 25, 200)); // Max 200%
  };

  const handlePdfZoomOut = () => {
    setPdfZoom((prev) => Math.max(prev - 25, 50)); // Min 50%
  };

  const handlePdfZoomReset = () => {
    setPdfZoom(100);
  };

  const handleImageZoomIn = () => {
    setImageZoom((prev) => Math.min(prev + 25, 200)); // Max 200%
  };

  const handleImageZoomOut = () => {
    setImageZoom((prev) => Math.max(prev - 25, 50)); // Min 50%
  };

  const handleImageZoomReset = () => {
    setImageZoom(100);
  };

  // Group questions - MATCHING questions with same matchingGroupId are grouped
  const groupedQuestions = React.useMemo(() => {
    const sorted = [...questions].sort(
      (a, b) => a.questionNumber - b.questionNumber
    );
    const groups: (Question | Question[])[] = [];

    let i = 0;
    while (i < sorted.length) {
      const current = sorted[i];

      if (current.questionType === "MATCHING") {
        const group: Question[] = [current];
        let j = i + 1;

        while (j < sorted.length && sorted[j].questionType === "MATCHING") {
          const next = sorted[j];

          const sameGroup =
            (current.matchingGroupId &&
              next.matchingGroupId === current.matchingGroupId) ||
            (!current.matchingGroupId &&
              !next.matchingGroupId &&
              current.questionText === next.questionText &&
              (current.imageUrl || current.imageDownloadUrl) ===
                (next.imageUrl || next.imageDownloadUrl));

          if (sameGroup) {
            group.push(next);
            j++;
          } else {
            break;
          }
        }

        if (group.length > 1) {
          groups.push(group);
          i = j;
        } else {
          groups.push(current);
          i++;
        }
      } else {
        groups.push(current);
        i++;
      }
    }

    return groups;
  }, [questions]);

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-gray-200">
      {/* Teil Header */}
      <div className="bg-gradient-to-r from-[#009fe3] to-[#0088cc] px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {part.displayName}
              {part.partTitle && (
                <span className="text-lg font-normal ml-2">
                  - {part.partTitle}
                </span>
              )}
            </h2>
            <p className="text-white/90 text-sm mt-1">
              {questions.length} Fragen • {part.totalPoints} Punkte
              {part.recommendedTimeMinutes && (
                <span className="ml-3">
                  ⏱️ Empfohlene Zeit: {part.recommendedTimeMinutes} Min.
                </span>
              )}
            </p>
          </div>
          <div className="text-right">
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all ${
                allAnswered
                  ? "bg-green-500 text-white shadow-lg"
                  : "bg-white/20 text-white"
              }`}
            >
              <span className="text-lg">
                {answeredCount}/{questions.length}
              </span>
              <span className="text-sm">beantwortet</span>
            </div>
          </div>
        </div>
      </div>

      {/* Teil Instructions (Aufgabenstellung) */}
      {part.instructions && (
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b-2 border-blue-100 px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="text-blue-600 text-3xl">📋</div>
            <div>
              <h3 className="font-bold text-blue-900 mb-2 text-lg">
                Aufgabenstellung:
              </h3>
              <p className="text-blue-800 leading-relaxed text-base">
                {part.instructions}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Shared Context Section */}
      {part.hasSharedContext && (
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-b-2 border-gray-200 px-3 sm:px-6 py-3 sm:py-5">
          <div className="space-y-4">
            {/* Shared Context Text */}
            {part.hasContextText && part.contextText && (
              <div className="bg-white rounded-xl p-3 sm:p-5 border-2 border-gray-300 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="text-gray-600 text-2xl sm:text-3xl">📄</div>
                  <div className="flex-1">
                    <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
                      {part.contextText}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Shared Context Image - Full-width on mobile with zoom */}
            {part.hasContextImage && part.contextImageDownloadUrl && (
              <div className="bg-white rounded-lg sm:rounded-xl overflow-hidden border-2 border-gray-300 shadow-sm">
                {/* Image Zoom Controls */}
                <div className="flex items-center justify-between gap-3 p-3 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    {/* Zoom Out */}
                    <button
                      onClick={handleImageZoomOut}
                      disabled={imageZoom <= 50}
                      className="p-2 sm:p-2.5 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      title="Verkleinern"
                    >
                      <FaSearchMinus className="text-gray-700 text-sm sm:text-base" />
                    </button>

                    {/* Zoom Level Display */}
                    <span className="min-w-[60px] sm:min-w-[70px] text-center font-bold text-[#009fe3] text-sm sm:text-base">
                      {imageZoom}%
                    </span>

                    {/* Zoom In */}
                    <button
                      onClick={handleImageZoomIn}
                      disabled={imageZoom >= 200}
                      className="p-2 sm:p-2.5 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      title="Vergrößern"
                    >
                      <FaSearchPlus className="text-gray-700 text-sm sm:text-base" />
                    </button>

                    {/* Reset Zoom */}
                    <button
                      onClick={handleImageZoomReset}
                      className="p-2 sm:p-2.5 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 transition-all"
                      title="Zurücksetzen"
                    >
                      <FaUndo className="text-gray-700 text-sm sm:text-base" />
                    </button>
                  </div>
                </div>

                {/* Image with Zoom */}
                <div
                  className="overflow-auto bg-gray-100"
                  style={{ maxHeight: "80vh" }}
                >
                  <div
                    style={{
                      transform: `scale(${imageZoom / 100})`,
                      transformOrigin: "top center",
                      transition: "transform 0.3s ease",
                    }}
                  >
                    <img
                      src={part.contextImageDownloadUrl}
                      alt="Gemeinsames Kontextbild"
                      className="w-full h-auto"
                      onError={(e) => {
                        console.error(
                          "Failed to load image:",
                          part.contextImageDownloadUrl
                        );
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Shared Context Document (PDF) - ✅ Inline preview with zoom */}
            {part.hasContextDocument && part.contextDocumentDownloadUrl && (
              <div className="bg-white rounded-xl overflow-hidden border-2 border-gray-300 shadow-sm">
                {/* Zoom Controls */}
                <div className="flex items-center justify-between gap-3 p-3 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    {/* Zoom Out */}
                    <button
                      onClick={handlePdfZoomOut}
                      disabled={pdfZoom <= 50}
                      className="p-2 sm:p-2.5 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      title="Verkleinern"
                    >
                      <FaSearchMinus className="text-gray-700 text-sm sm:text-base" />
                    </button>

                    {/* Zoom Level Display */}
                    <span className="min-w-[60px] sm:min-w-[70px] text-center font-bold text-[#009fe3] text-sm sm:text-base">
                      {pdfZoom}%
                    </span>

                    {/* Zoom In */}
                    <button
                      onClick={handlePdfZoomIn}
                      disabled={pdfZoom >= 200}
                      className="p-2 sm:p-2.5 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      title="Vergrößern"
                    >
                      <FaSearchPlus className="text-gray-700 text-sm sm:text-base" />
                    </button>

                    {/* Reset Zoom */}
                    <button
                      onClick={handlePdfZoomReset}
                      className="p-2 sm:p-2.5 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-100 transition-all"
                      title="Zurücksetzen"
                    >
                      <FaUndo className="text-gray-700 text-sm sm:text-base" />
                    </button>
                  </div>
                </div>

                {/* PDF Inline Preview */}
                <div
                  className="relative w-full bg-gray-100"
                  style={{ minHeight: "600px" }}
                >
                  <div
                    className="w-full h-full overflow-auto"
                    style={{
                      minHeight: "600px",
                      maxHeight: "80vh",
                    }}
                  >
                    <div
                      style={{
                        transform: `scale(${pdfZoom / 100})`,
                        transformOrigin: "top center",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <iframe
                        src={`${part.contextDocumentDownloadUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                        className="w-full border-0"
                        style={{
                          minHeight: "600px",
                          height: "80vh",
                        }}
                        title="Dokument"
                      />
                    </div>
                  </div>
                </div>

                {/* Fallback: Download link for browsers that don't support iframe */}
                <div className="p-3 bg-gray-50 border-t border-gray-200 text-center">
                  <a
                    href={part.contextDocumentDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#009fe3] hover:text-[#0088cc] font-semibold inline-flex items-center gap-2"
                  >
                    <span>📥</span>
                    <span>
                      Falls das Dokument nicht angezeigt wird, hier klicken um
                      es in neuem Tab zu öffnen
                    </span>
                  </a>
                </div>
              </div>
            )}

            {/* Shared Context Audio */}
            {part.hasContextAudio && part.contextAudioDownloadUrl && (
              <div className="bg-white rounded-xl p-3 sm:p-5 border-2 border-indigo-300 shadow-md">
                <div className="flex items-start gap-3">
                  <div className="text-indigo-600 text-2xl sm:text-3xl">🎧</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-indigo-900 mb-3 text-sm sm:text-base">
                      Audio für alle Fragen:
                    </h4>

                    <audio
                      controls
                      className="w-full rounded-lg"
                      src={part.contextAudioDownloadUrl}
                      onError={(e) => {
                        console.error(
                          "❌ Failed to load audio:",
                          part.contextAudioDownloadUrl
                        );
                      }}
                    >
                      Ihr Browser unterstützt das Audio-Element nicht.
                    </audio>
                    <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <p className="text-xs sm:text-sm text-indigo-800 font-medium">
                        💡 Hören Sie sich das Audio an, bevor Sie die Fragen
                        beantworten.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Questions Section */}
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
        {questions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <div className="text-4xl mb-3">📝</div>
            <p className="font-semibold">Keine Fragen in diesem Teil.</p>
          </div>
        ) : (
          groupedQuestions.map((item, index) => {
            if (Array.isArray(item)) {
              const matchingQuestions = item;
              return (
                <MatchingQuestionsGroup
                  key={`matching-group-${
                    matchingQuestions[0].matchingGroupId ||
                    matchingQuestions[0].id
                  }-${index}`}
                  questions={matchingQuestions}
                  answers={answers}
                  onAnswerChange={onAnswerChange}
                  disabled={disabled}
                />
              );
            } else {
              const question = item;
              return (
                <QuestionDisplay
                  key={question.id}
                  question={question}
                  answer={answers.get(question.id) || ""}
                  onAnswerChange={(ans) => onAnswerChange(question.id, ans)}
                  disabled={disabled}
                />
              );
            }
          })
        )}
      </div>

      {/* Teil Footer - Progress Summary */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-t-2 border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <span className="text-sm sm:text-base text-gray-700 font-semibold">
            {part.shortDisplayName} Fortschritt:
          </span>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex-1 bg-gray-300 rounded-full h-2 sm:h-3 w-32 sm:w-40 overflow-hidden">
              <div
                className={`h-2 sm:h-3 rounded-full transition-all duration-500 ${
                  allAnswered
                    ? "bg-gradient-to-r from-green-500 to-emerald-500"
                    : "bg-gradient-to-r from-[#009fe3] to-cyan-500"
                }`}
                style={{
                  width: `${(answeredCount / questions.length) * 100}%`,
                }}
              />
            </div>
            <span
              className={`font-bold text-base sm:text-lg ${
                allAnswered ? "text-green-600" : "text-gray-700"
              }`}
            >
              {answeredCount}/{questions.length}
            </span>
            {allAnswered && (
              <span className="text-green-600 text-lg sm:text-xl">✓</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeilDisplay;
